import { useEffect, useState } from 'react'

import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Boton } from '@/shared/ui/Boton/Boton'
import { Campo } from '@/shared/ui/Formulario/Campo'
import { Selector } from '@/shared/ui/Formulario/Selector'
import { Modal } from '@/shared/ui/Modal/Modal'

import { CargarRut } from './CargarRut'
import {
  useCiudadesWo,
  useCrearCliente,
  useEsperarCliente,
  useTiposIdentificacion,
} from './useCrearCliente'
import estilos from './NuevoCliente.module.css'

type PropsNuevoCliente = {
  abierto: boolean
  onCerrar: () => void
  /** Se llama con el tercero creado, para seguir el trabajo sin buscarlo. */
  onCreado?: (idTercero: number, nombre: string) => void
}

/**
 * Crear un cliente en World Office.
 *
 * Hasta ahora, si el cliente no existía allá no se podía ni cotizar ni crear
 * la orden: había que salir de la aplicación. La contabilidad sigue siendo la
 * dueña del tercero — solo que ya no hace falta ir hasta ella.
 */
export function NuevoCliente({ abierto, onCerrar, onCreado }: PropsNuevoCliente) {
  const tipos = useTiposIdentificacion()
  const crear = useCrearCliente()

  const [tipo, setTipo] = useState('31')
  const [identificacion, setIdentificacion] = useState('')
  const [nombre, setNombre] = useState('')
  const [apellidos, setApellidos] = useState('')
  // 0262: lo que la factura necesita para llegarle al cliente.
  const [direccion, setDireccion] = useState('')
  const [ciudad, setCiudad] = useState('76147') // Cartago
  const [telefono, setTelefono] = useState('')
  const [correo, setCorreo] = useState('')
  // El régimen que escogió la persona. Vacío = el que corresponde al tipo de
  // documento (NIT → persona jurídica, cédula → persona natural no
  // responsable de IVA), que es el que pone World Office si no se le dice
  // otro. Se muestra el valor real, no «el normal» (Johan: «¿cómo que el
  // normal para el NIT?»).
  const [regimen, setRegimen] = useState('')
  const ciudades = useCiudadesWo()
  const [outboxId, setOutboxId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const solicitud = useEsperarCliente(outboxId)

  const tipoElegido = tipos.find((t) => String(t.codigo) === tipo)
  // Una empresa lleva todo en el nombre; una persona natural se separa,
  // porque World Office guarda nombres y apellidos aparte.
  const esEmpresa = tipoElegido?.es_empresa ?? false
  const regimenVisible = regimen || (esEmpresa ? '3' : '6')

  // Al cambiar el tipo de documento, el régimen vuelve al que le corresponde.
  useEffect(() => setRegimen(''), [tipo])

  useEffect(() => {
    if (!abierto) {
      setIdentificacion('')
      setNombre('')
      setApellidos('')
      setDireccion('')
      setCiudad('76147')
      setTelefono('')
      setCorreo('')
      setRegimen('')
      setOutboxId(null)
      setError(null)
    }
  }, [abierto])

  // Al llegar el tercero se avisa a quien abrió el formulario.
  useEffect(() => {
    if (solicitud?.estado === 'done' && solicitud.wo_id_tercero) {
      onCreado?.(solicitud.wo_id_tercero, solicitud.nombre)
    }
  }, [solicitud, onCreado])

  async function enviar() {
    setError(null)

    try {
      const id = await crear.mutateAsync({
        tipo: Number(tipo),
        identificacion,
        nombre,
        apellidos: esEmpresa ? undefined : apellidos,
        direccion,
        telefono,
        email: correo,
        ciudad: ciudad ? Number(ciudad) : null,
        propiedadRetencion: regimen ? Number(regimen) : null,
      })
      setOutboxId(id)
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No fue posible crear el cliente.')
    }
  }

  // ── Esperando a World Office ──────────────────────────────────────────
  if (outboxId) {
    const listo = solicitud?.estado === 'done'
    const fallo = solicitud?.estado === 'error'

    return (
      <Modal
        abierto={abierto}
        titulo={listo ? 'Cliente creado' : 'Creando en World Office'}
        onCerrar={onCerrar}
        pie={
          <Boton variante="principal" onClick={onCerrar}>
            {listo ? 'Listo' : 'Cerrar'}
          </Boton>
        }
      >
        <div className={estilos.espera}>
          {listo ? (
            <>
              <p className={estilos.exito}>{solicitud?.nombre}</p>
              <p className={estilos.texto}>
                {solicitud?.ya_existia
                  ? 'Ya existía en World Office con ese documento, así que se usó el que había: no se creó ninguno repetido.'
                  : 'Quedó creado en World Office y ya se puede usar para cotizar o crear una orden.'}
              </p>
              <p className={estilos.reloj}>Tercero {solicitud?.wo_id_tercero}</p>
            </>
          ) : fallo ? (
            <Aviso tipo="error" titulo="World Office rechazó la creación">
              {solicitud?.error_msg ?? 'Sin detalle.'}
            </Aviso>
          ) : (
            <>
              <span className={estilos.girador} aria-hidden="true" />
              <p className={estilos.titulo}>Creando el cliente</p>
              <p className={estilos.texto}>
                World Office lo registra con sus responsabilidades tributarias y su lista de
                precios.
              </p>
              <p className={estilos.reloj}>{solicitud?.segundos ?? 0} s</p>
            </>
          )}
        </div>
      </Modal>
    )
  }

  // ── El formulario ─────────────────────────────────────────────────────
  // El correo es donde le llega la factura electrónica. Para una empresa es
  // obligatorio; a una persona natural se le puede facturar sin él, pero no
  // la recibe (WORKERS MEGA, 0263).
  const correoValido = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(correo.trim())
  const correoBien = correo.trim() ? correoValido : !esEmpresa
  const listoParaEnviar =
    identificacion.trim().length > 0 &&
    nombre.trim().length > 0 &&
    (esEmpresa || apellidos.trim()) &&
    direccion.trim().length > 0 &&
    correoBien

  return (
    <Modal
      abierto={abierto}
      titulo="Nuevo cliente"
      subtitulo="Se crea en World Office, que es donde viven los terceros."
      onCerrar={onCerrar}
      pie={
        <>
          {/* EL MOTIVO, AL LADO DEL BOTÓN QUE LO PROVOCÓ.
              Estaba arriba del formulario, y en un teléfono eso queda fuera de
              pantalla: se pulsa «Crear cliente» abajo del todo y parece que no
              pasó nada. Ya ocurrió una vez con el cotizador. */}
          {error && (
            <span className={estilos.motivo}>
              <Aviso tipo="error">{error}</Aviso>
            </span>
          )}

          <Boton onClick={onCerrar}>Cancelar</Boton>
          <Boton
            variante="principal"
            onClick={() => void enviar()}
            trabajando={crear.isPending}
            disabled={!listoParaEnviar}
          >
            Crear cliente
          </Boton>
        </>
      }
    >
      {/* El camino corto: se suelta el RUT y los campos quedan llenos. Los
          campos siguen abajo porque lo leído hay que poder corregirlo. */}
      <CargarRut
        onLeido={(leido) => {
          setIdentificacion(leido.identificacion)
          setNombre(leido.nombre)
          setApellidos(leido.apellidos)
          if (leido.direccion) setDireccion(leido.direccion)
          if (leido.telefono) setTelefono(leido.telefono)
          if (leido.correo) setCorreo(leido.correo.toLowerCase())
          // La ciudad del RUT viene escrita: se busca entre las que World
          // Office conoce, sin tildes ni mayúsculas.
          const sinTildes = (t: string) =>
            t
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .toLowerCase()
              .trim()
          const enWo = ciudades.find((c) => sinTildes(c.ciudad) === sinTildes(leido.ciudad))
          if (enWo) setCiudad(String(enWo.id_ciudad))
          // NIT para empresa, cédula para persona: es lo que el RUT dice al
          // traer o no traer razón social.
          setTipo(leido.esEmpresa ? '31' : '13')
        }}
      />

      <div className={estilos.rejilla}>
        <Selector
          etiqueta="Tipo de documento"
          obligatorio
          opciones={tipos.map((t) => ({ valor: String(t.codigo), etiqueta: t.nombre }))}
          value={tipo}
          onChange={(e) => setTipo(e.target.value)}
          textoVacio="Seleccione…"
        />

        <Campo
          etiqueta="Número de documento"
          obligatorio
          value={identificacion}
          onChange={(e) => setIdentificacion(e.target.value)}
          ayuda={
            esEmpresa
              ? 'Sin el dígito de verificación: World Office lo calcula.'
              : 'Sin puntos ni guiones.'
          }
        />

        <div className={estilos.completo}>
          <Campo
            etiqueta={esEmpresa ? 'Razón social' : 'Nombres'}
            obligatorio
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            ayuda={esEmpresa ? 'Como debe aparecer en la cotización y en la factura.' : undefined}
          />
        </div>

        {!esEmpresa && (
          <div className={estilos.completo}>
            <Campo
              etiqueta="Apellidos"
              obligatorio
              value={apellidos}
              onChange={(e) => setApellidos(e.target.value)}
              ayuda="World Office guarda nombres y apellidos por separado."
            />
          </div>
        )}

        <div className={estilos.completo}>
          <Campo
            etiqueta="Dirección"
            obligatorio
            value={direccion}
            onChange={(e) => setDireccion(e.target.value)}
            ayuda="La que va en la factura."
          />
        </div>

        <Selector
          etiqueta="Ciudad"
          obligatorio
          opciones={ciudades.map((c) => ({ valor: String(c.id_ciudad), etiqueta: c.ciudad }))}
          value={ciudad}
          onChange={(e) => setCiudad(e.target.value)}
          textoVacio="Seleccione…"
        />

        <Campo
          etiqueta="Celular"
          value={telefono}
          onChange={(e) => setTelefono(e.target.value)}
          inputMode="tel"
          ayuda="Por aquí le llegan la cotización y el acta por WhatsApp."
        />

        <div className={estilos.completo}>
          <Campo
            etiqueta="Correo"
            obligatorio={esEmpresa}
            type="email"
            value={correo}
            onChange={(e) => setCorreo(e.target.value)}
            ayuda={
              correo.trim() && !correoValido
                ? 'Ese correo no parece válido.'
                : esEmpresa
                ? 'Ahí le llega la factura electrónica.'
                : 'Ahí le llega la factura electrónica. Sin correo se le factura igual, pero no la recibe.'
            }
          />
        </div>

        <div className={estilos.completo}>
          <Selector
            etiqueta="Régimen tributario"
            ayuda={`En World Office es la «propiedad de retención». Con ${
              esEmpresa ? 'NIT' : 'cédula'
            } se pone esta; cámbiela solo si el RUT dice otra cosa.`}
            opciones={[
              { valor: '3', etiqueta: 'Persona jurídica' },
              { valor: '6', etiqueta: 'Persona natural no responsable de IVA' },
              { valor: '2', etiqueta: 'Persona natural responsable de IVA' },
              { valor: '7', etiqueta: 'Persona jurídica autorretenedora' },
              { valor: '16', etiqueta: 'Régimen simple · persona jurídica' },
              { valor: '17', etiqueta: 'Régimen simple · persona natural' },
            ]}
            value={regimenVisible}
            onChange={(e) => setRegimen(e.target.value)}
          />
        </div>
      </div>

      <div className={estilos.nota}>
        <Aviso tipo="info">
          Se crea marcado como cliente, con la lista de Precio 1. Las responsabilidades tributarias
          salen del régimen, y el dígito de verificación del NIT lo calcula World Office.
        </Aviso>
      </div>
    </Modal>
  )
}
